import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Trash2, PenLine, PackagePlus, Plus, Save, FileText, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { tk, bn, money, todayStr } from "../lib/format";
import { PageHeader, Field, Modal, Empty, Badge } from "../components/kit";
import ProductSearch from "../components/ProductSearch";
import ProductForm from "../components/ProductForm";
import { CompanyForm } from "./Companies";

let KEY = 1;

function Uploads({ files, setFiles }: { files: any[]; setFiles: (f: any) => void }) {
  const [busy, setBusy] = useState(false);
  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads", fd);
        setFiles((a: any[]) => [...a, { ...data, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : "" }]);
      } catch (er) {
        toast.error(`${f.name}: ${errMsg(er)}`);
      }
    }
    setBusy(false);
  };
  return (
    <div>
      <label className={`flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-4 py-6 cursor-pointer hover:bg-blue-50 ${busy ? "opacity-60" : ""}`}>
        <Upload className="text-blue-700" size={22} />
        <span className="font-semibold text-blue-800">{busy ? "আপলোড হচ্ছে..." : "চালানের ছবি / PDF আপলোড"}</span>
        <span className="text-xs text-slate-500">JPG, PNG, WEBP, PDF · সর্বোচ্চ ১০ MB</span>
        <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={onPick} disabled={busy} data-testid="purchase-challan-upload-input" />
      </label>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="purchase-uploaded-files">
          {files.map((f) => (
            <div key={f.id} className="relative w-24 rounded-lg border border-slate-200 overflow-hidden bg-white">
              <div className="h-20 grid place-items-center bg-slate-50">{f.preview ? <img src={f.preview} alt="" className="w-full h-full object-cover" /> : <FileText className="text-red-500" />}</div>
              <p className="text-[10px] px-1.5 py-0.5 truncate">{f.original_filename}</p>
              <button type="button" onClick={() => setFiles((a: any[]) => a.filter((x) => x.id !== f.id))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 grid place-items-center shadow" aria-label="সরান"><X size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PurchaseCreate() {
  const nav = useNavigate();
  const [companies, setCompanies] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState("");
  const [challan, setChallan] = useState("");
  const [date, setDate] = useState(todayStr());
  const [items, setItems] = useState<any[]>([]);
  const [paid, setPaid] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<any[]>([]);
  const [quick, setQuick] = useState<any>(null);
  const [addCo, setAddCo] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)); }, []);

  const add = (p: any) => setItems((l) => {
    const ex = l.find((x) => x.product_id === p.id);
    if (ex) return l.map((x) => (x === ex ? { ...x, qty: (+x.qty || 0) + 1 } : x));
    return [...l, { key: KEY++, product_id: p.id, name: p.name, qty: 1, unit: p.unit, rate: p.purchase_rate || "" }];
  });
  const upd = (k: number, p: any) => setItems((l) => l.map((x) => (x.key === k ? { ...x, ...p } : x)));
  const total = useMemo(() => items.reduce((s, it) => s + (+it.qty || 0) * (+it.rate || 0), 0), [items]);
  const paidN = +paid || 0;
  const due = total - paidN;
  const status = paidN >= total && total > 0 ? ["পরিশোধিত", "green"] : paidN > 0 ? ["আংশিক বাকি", "amber"] : ["বাকিতে নেওয়া", "red"];

  const save = async () => {
    if (!companyId) return toast.error("কোম্পানি বাছাই করুন");
    if (!items.length || items.some((it) => !String(it.name).trim() || !(+it.qty > 0))) return toast.error("প্রতিটি লাইনে পণ্যের নাম ও পরিমাণ দিন");
    setBusy(true);
    try {
      await api.post("/purchases", {
        company_id: companyId, challan_no: challan, date, paid: paidN, note, file_ids: files.map((f) => f.id),
        items: items.map((it) => ({ product_id: it.product_id, name: String(it.name).trim(), qty: +it.qty, unit: it.unit, rate: +it.rate || 0 })),
      });
      toast.success("ক্রয় এন্ট্রি সেভ হয়েছে, স্টক বেড়েছে");
      nav("/purchases");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="purchase-create-page">
      <PageHeader title="ক্রয় এন্ট্রি" subtitle="কোম্পানি থেকে মাল আসলে এখানে এন্ট্রি দিন — স্টক স্বয়ংক্রিয়ভাবে বাড়বে" />
      <div className="grid xl:grid-cols-[1fr_360px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          <div className="card p-5 grid md:grid-cols-3 gap-4">
            <Field label="কোম্পানি *">
              <div className="flex gap-2">
                <select className="inp" value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="purchase-company-select">
                  <option value="">বাছাই করুন</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button type="button" className="btn btn-ghost px-3" onClick={() => setAddCo(true)} data-testid="purchase-add-company-button" aria-label="নতুন কোম্পানি"><Plus size={18} /></button>
              </div>
            </Field>
            <Field label="চালান নং"><input className="inp" value={challan} onChange={(e) => setChallan(e.target.value)} data-testid="purchase-challan-no-input" /></Field>
            <Field label="তারিখ"><input className="inp" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="purchase-date-input" /></Field>
          </div>
          <div className="card p-5"><Uploads files={files} setFiles={setFiles} /></div>
          <div className="card p-5">
            <ProductSearch onPick={add} onQuickAdd={(name: string) => setQuick({ name, company: companies.find((c) => c.id === companyId)?.name || "" })} testid="purchase-product-search-input" />
            <div className="flex flex-wrap gap-2 mt-3">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setItems((l) => [...l, { key: KEY++, product_id: null, name: "", qty: 1, unit: "পিস", rate: "" }])} data-testid="purchase-add-manual-line"><PenLine size={15} /> ম্যানুয়াল লাইন</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQuick({ company: companies.find((c) => c.id === companyId)?.name || "" })} data-testid="purchase-quick-add-product"><PackagePlus size={15} /> দ্রুত পণ্য যোগ</button>
            </div>
            <div className="mt-4 overflow-x-auto">
              {items.length === 0 ? <Empty text="পণ্য যোগ করুন" /> : (
                <table className="tbl" data-testid="purchase-items-editor">
                  <thead><tr><th>পণ্য</th><th>পরিমাণ</th><th>একক</th><th>ক্রয় দর (৳)</th><th className="text-right">মোট</th><th /></tr></thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={it.key}>
                        <td className="min-w-[180px]">{it.product_id ? <b>{it.name}</b> : <input className="inp inp-sm" value={it.name} placeholder="পণ্যের নাম (স্টকে যোগ হবে না)" onChange={(e) => upd(it.key, { name: e.target.value })} data-testid={`purchase-line-name-${i}`} />}</td>
                        <td><input className="inp inp-sm w-20 num" type="number" step="any" min="0" value={it.qty} onChange={(e) => upd(it.key, { qty: e.target.value })} data-testid={`purchase-line-qty-${i}`} /></td>
                        <td><input className="inp inp-sm w-20" value={it.unit} onChange={(e) => upd(it.key, { unit: e.target.value })} /></td>
                        <td><input className="inp inp-sm w-24 num" type="number" step="any" min="0" value={it.rate} onChange={(e) => upd(it.key, { rate: e.target.value })} data-testid={`purchase-line-rate-${i}`} /></td>
                        <td className="text-right num font-semibold">{money((+it.qty || 0) * (+it.rate || 0))}</td>
                        <td><button type="button" className="w-8 h-8 grid place-items-center rounded-lg text-red-500 hover:bg-red-50" onClick={() => setItems((l) => l.filter((x) => x.key !== it.key))} aria-label="মুছুন"><Trash2 size={16} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
        <div className="card p-5 xl:sticky xl:top-20 space-y-4" data-testid="purchase-summary-panel">
          <h2 className="text-lg font-semibold">ক্রয়ের হিসাব</h2>
          <div className="flex justify-between text-lg"><span>মোট ক্রয়</span><b className="num text-blue-800" data-testid="purchase-total-display">{tk(total)}</b></div>
          <Field label="কোম্পানিকে পরিশোধ (৳)">
            <div className="flex gap-2">
              <input className="inp num font-semibold" type="number" min="0" step="any" value={paid} onChange={(e) => setPaid(e.target.value)} placeholder="0" data-testid="purchase-paid-input" />
              <button type="button" className="btn btn-ghost btn-sm h-11" onClick={() => setPaid(String(+total.toFixed(2)))} data-testid="purchase-pay-full">সব</button>
            </div>
          </Field>
          <div className={`rounded-lg px-4 py-3 flex justify-between ${due > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
            <span className="font-semibold">বাকি (দেনা)</span><b className="num text-xl" data-testid="purchase-due-display">{tk(due)}</b>
          </div>
          <div className="flex justify-between items-center text-sm"><span>অবস্থা</span><Badge tone={status[1]}>{status[0]}</Badge></div>
          <Field label="নোট"><input className="inp" value={note} onChange={(e) => setNote(e.target.value)} data-testid="purchase-note-input" /></Field>
          {paidN > total && <p className="text-sm text-red-600">পরিশোধ মোট ক্রয়ের চেয়ে বেশি</p>}
          <button className="btn btn-primary w-full" onClick={save} disabled={busy || paidN > total} data-testid="purchase-save-button"><Save size={17} /> {busy ? "সেভ হচ্ছে..." : `ক্রয় সেভ করুন (${bn(items.length)} টি পণ্য)`}</button>
        </div>
      </div>
      <Modal open={!!quick} onClose={() => setQuick(null)} title="দ্রুত নতুন পণ্য যোগ" testid="purchase-quick-add-modal">
        {quick && <ProductForm compact initial={{ ...quick, stock: "" }} onCancel={() => setQuick(null)} onSaved={(p) => { add(p); setQuick(null); }} />}
      </Modal>
      <Modal open={addCo} onClose={() => setAddCo(false)} title="নতুন কোম্পানি" testid="purchase-company-modal">
        <CompanyForm onDone={(c) => { setCompanies((l) => [...l, c]); setCompanyId(c.id); setAddCo(false); }} />
      </Modal>
    </div>
  );
}
